/**
 * Strips every key whose value is `null` or `undefined` from a plain object before it's sent as
 * a request body. Exists because the same bug class was found and independently fixed at least
 * four separate times in one session (billing plans, the pricing simulator, event creation,
 * promo codes, custom questions): a form sends `null` for a blank/not-applicable optional field,
 * and the API's zod `.optional()` schema rejects it - `.optional()` only ever accepts a MISSING
 * key or `undefined`, never an explicit `null`. Rather than keep fixing this one call site at a
 * time, callers building a request body should run it through this first.
 *
 * Deliberately shallow (one level only) - a nested object's own `null`/`undefined` fields are
 * left alone, since stripping those would change the nested shape a schema expects rather than
 * just omitting an absent top-level field. Deliberately keeps `0`, `false`, and `''` - those are
 * real values, not absence, and must reach the server as sent.
 */
export function omitNullish<T extends Record<string, unknown>>(
  obj: T,
): { [K in keyof T]?: Exclude<T[K], null | undefined> } {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && value !== undefined) {
      result[key] = value;
    }
  }
  return result as { [K in keyof T]?: Exclude<T[K], null | undefined> };
}
